"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { getEbooks } from "@/data/ebooks";

export default function EbooksIndexPage() {
  const t = useTranslations("ebooks.index");
  const tData = useTranslations("ebooksData");
  const ebooks = getEbooks((slug) => tData.raw(slug));

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-surface-dark-raised dark:via-surface-dark-raised dark:to-surface-dark text-gray-900 dark:text-gray-100">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="h-full w-full bg-[radial-gradient(circle_at_30%_30%,rgba(208,27,23,0.08),transparent_45%),radial-gradient(circle_at_70%_20%,rgba(12,59,82,0.1),transparent_40%)]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <p className="text-sm uppercase tracking-label text-red-600 dark:text-red-400 font-semibold mb-3">
            {t("subtitle")}
          </p>
          <h1 className="type-h1">{t("title")}</h1>
          <p className="mt-4 max-w-3xl text-lg text-gray-700 dark:text-gray-300">{t("description")}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="grid gap-8 md:gap-10">
          {ebooks.map((ebook) => (
            <Link
              key={ebook.slug}
              href={`/ebooks-gratuitos/${ebook.slug}`}
              className="media-frame group flex flex-col md:flex-row gap-6 md:gap-8 border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-surface-dark-raised dark:shadow-card-dark hover:-translate-y-0.5 transition-transform duration-200"
            >
              <div className="relative w-full md:w-56 lg:w-64 shrink-0 bg-gradient-to-br from-gray-100 to-white dark:from-[#0d1728] dark:to-[#0a0f1c] flex items-center justify-center p-6">
                {ebook.badge && (
                  <span className="absolute left-4 top-4 rounded-full bg-red-600 text-white px-3 py-1 text-xs font-semibold shadow-lg">
                    {ebook.badge}
                  </span>
                )}
                <div className="relative h-64 w-full">
                  <Image
                    src={ebook.cover}
                    alt={ebook.title}
                    fill
                    className="object-contain rounded-xl drop-shadow-2xl"
                    sizes="(max-width: 768px) 70vw, 240px"
                  />
                </div>
              </div>
              <div className="flex-1 py-8 pr-6 pl-6 md:pl-0 flex flex-col gap-4">
                <h2 className="text-gray-900 dark:text-white type-h2">
                  {ebook.title}
                </h2>
                <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
                  {ebook.description}
                </p>
                {ebook.highlights && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ebook.highlights.slice(0, 4).map((highlight, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-100 px-3 py-1 text-xs font-semibold"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-auto">
                  <span className="inline-flex items-center gap-2 text-red-600 dark:text-red-300 font-semibold">
                    {t("cta")}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
